// AI chat assistant wire protocol, sizing constants, and model tool schemas
// (AI_ASSISTANT_PLAN.md Phase 2, D-A1..D-A12).
//
// Shared by the streaming Lambda (api/src/aiChat.ts) and the browser dock, so
// the SSE event union has exactly one definition.
//
// INVARIANT (I-2): AI_CHAT_TOOLS is a single, fixed-order array sent in full on
// every request, from every page, for every user. Bedrock prompt caching is a
// strict prefix match, so building the array conditionally — or reordering it —
// invalidates the cached tool prefix on every context switch. Phase 3 APPENDS
// its seven write tools after the four read tools below; it never reorders them.
// AI_ASSISTANT_REMEDIATION_PLAN Task 5.3 appends two KB tools and two card tools
// after those eleven, for the same reason.

import { LIMITS, type AiChatTranscriptMode, type ListKind, type ResourceKind } from "./types.js";

export const AI_CHAT_LIMITS = {
  /** Per-user message length. Longer inputs are the cheapest thing to bound. */
  messageMax: 2000,
  /** Model rounds inside ONE request. Every round that makes a tool call -
   *  including a write-only one - gets its results back and another round, so the
   *  model can chain proposals and then describe what the card actually holds.
   *  6 covers "search -> list -> list -> template -> checklist -> wrap-up" even
   *  when the model makes its proposal calls one at a time instead of in parallel. */
  maxToolRounds: 6,
  /** Bedrock max_tokens. A split_list proposal emitting 5 lists + a template is
   *  the largest realistic output and lands around 2.5k tokens; 4096 leaves margin
   *  without paying for it (output is billed only when produced). */
  maxOutputTokens: 4096,
  /** Turns kept in the prompt. Older turns are dropped in blocks (see below). */
  historyTurnsMax: 20,
  /** Trim granularity. Dropping ONE turn at a time changes the message prefix on
   *  every single turn and destroys the history cache; dropping 6 at once means
   *  the prefix is stable for the next 6 turns. */
  historyTrimBlock: 6,
  /** Items of the open resource put into the volatile system block. Above this we
   *  send a truncated view plus a count, and the model uses read tools for detail. */
  contextItemsMax: 200,
  /** Results returned by each grounding tool. */
  searchResultsMax: 20,
  communityResultsMax: 10,
  /** Mutation ops in one proposal. */
  proposalOpsMax: 60,
  proposalTtlSeconds: 2 * 60 * 60,
  sessionTtlSeconds: 30 * 24 * 60 * 60,
  /** Undo snapshots expire 30 days after the proposal is applied. */
  undoTtlSeconds: 30 * 24 * 60 * 60,
  /** Previous conversations returned by GET /ai/sessions. */
  sessionListMax: 50,
  /** User messages per session before TURN_LIMIT (D-R11). User messages = nextSeq / 2. */
  turnsMax: 30,
} as const;

/** Default chat model (D-A11) — an existing ALLOWED_BEDROCK_MODEL_IDS entry. */
export const DEFAULT_AI_CHAT_MODEL_ID = "anthropic.claude-haiku-4-5";

/** Default per-user daily chat token budget (D-A12). ~$0.20/day worst case. */
export const DEFAULT_AI_CHAT_DAILY_TOKEN_LIMIT = 200_000;

// ---- Wire protocol ----

/** Which resource the dock is open on, if any. */
export interface AiChatContextRef {
  kind: "list" | "template" | "checklist";
  id: string;
}

export interface AiChatRequest {
  /** Omit to resume via the CHATREF# pointer, or start a fresh session. */
  sessionId?: string;
  /**
   * When true and sessionId is omitted, mint a new session instead of resuming
   * the CHATREF# pointer for the open resource.
   */
  forceNew?: boolean;
  message: string;
  context?: AiChatContextRef | null;
}

/** Why GET /ai/status reports the assistant degraded, for the dock's copy (SA10: never a dollar or token figure). */
export type AiAssistantDegradedReason = "SITE_BUDGET" | "USER_BUDGET";

/** GET /ai/status response. tier 0 means render nothing; degraded still renders the dock. */
export interface AiAssistantStatus {
  tier: 0 | 3;
  mode: "full" | "degraded";
  reason?: AiAssistantDegradedReason;
  /** Present only for tier 3 (SA15 setting, D-R12): whether transcripts keep text. */
  transcriptMode?: AiChatTranscriptMode;
}

// ---- Daily assistant metrics (F-15, SA18; AI_ASSISTANT_REMEDIATION_PLAN Task 3.4) ----

/** The daily counter set aggregated per UTC day / profile (ASST#AGG#{day} / PROFILE#{profile}). */
export const ASSISTANT_COUNTERS = [
  "conversations",
  "messages",
  "inputTokens",
  "outputTokens",
  "cacheReadTokens",
  "cacheWriteTokens",
  "estCostMicros",
  "lat_lt500",
  "lat_lt1000",
  "lat_lt2000",
  "lat_lt5000",
  "lat_ge5000",
  "refusals",
  "budgetHitsUser",
  "budgetHitsSite",
  "degradedServed",
  "ticketsDrafted",
  "ticketsSubmitted",
  "proposalsOffered",
  "proposalsApplied",
  "proposalsUndone",
  "kbMisses",
  "thumbsUp",
  "thumbsDown",
  "unresolvedConversations",
  "opens_list",
  "opens_template",
  "opens_checklist",
  "opens_other",
] as const;

export type AssistantCounter = (typeof ASSISTANT_COUNTERS)[number];

export interface AssistantDailyRow extends Partial<Record<AssistantCounter, number>> {
  date: string;
}

/** POST /ai/outcome body (SA18): best-effort client-reported outcomes. */
export type AiOutcomeEvent =
  | { event: "thumbs_up" | "thumbs_down"; sessionId: string; seq: number }
  | { event: "ticket_submitted"; sessionId?: string }
  | { event: "open"; surface: "list" | "template" | "checklist" | "other" };

export interface AdminAssistantMetrics {
  days: AssistantDailyRow[];
  flags: { code: string; count: number }[];
  monthToDateCostUsd: number;
  monthlyCapUsd: number;
}

/** One row in GET /ai/sessions (no turns). */
export interface AiChatSessionSummary {
  sessionId: string;
  title: string;
  resourceKind?: ResourceKind;
  resourceId?: string;
  updatedAt: string;
}

// ---- Proposals (D-A3) ----
//
// A mutation tool NEVER writes. It emits one AiProposalOp, which is stored under
// PROPOSAL#{id} and applied only when the user presses Apply, through the same
// updateList / updateTemplate / updateChecklist seams a human edit uses.

/**
 * The resource an op mutates, plus the version it was built against.
 *
 * INVARIANT (I-4): `version` is captured when the proposal is built and passed
 * back verbatim on apply. Dropping it makes parseVersion(undefined) skip the
 * conditional check entirely, silently clobbering a concurrent collaborator.
 */
export interface AiProposalTarget {
  kind: ResourceKind;
  id: string;
  /** Absent only for a target that could not be read when the proposal was built. */
  version?: number;
}

/** One item-level edit. Items are addressed BY TEXT, never by id (see below). */
export type AiItemChange =
  | { op: "add"; text: string; note?: string; group?: string }
  | { op: "remove"; text: string }
  | { op: "edit"; text: string; newText?: string; note?: string };

export interface AiProposalGroup {
  name: string;
  items: string[];
}

export type AiProposalTemplateSection =
  | { type: "list"; listId: string }
  | { type: "inline"; name: string; items: string[] };

export interface AiProposalChecklistSection {
  name: string;
  items: string[];
}

/**
 * One reviewable mutation.
 *
 * Item addressing is BY TEXT, not by id: the model does not reliably echo UUIDs.
 * The server resolves text -> id at apply time with a normalized (trim / collapse
 * whitespace / lowercase) match, and reports unmatched texts rather than failing
 * the whole apply.
 */
export type AiProposalOp =
  | { op: "items_change"; target: AiProposalTarget; changes: AiItemChange[] }
  | {
      op: "grouping";
      target: AiProposalTarget;
      subgroups: AiProposalGroup[];
      ungrouped: string[];
      /** Limit the regroup to this named section (checklist or template). Omit to regroup everything. */
      section?: string;
    }
  | {
      op: "metadata";
      target: AiProposalTarget;
      name?: string;
      description?: string;
      tags?: string[];
    }
  | {
      op: "new_list";
      name: string;
      description?: string;
      tags?: string[];
      kind: ListKind;
      items: string[];
      subgroups: AiProposalGroup[];
    }
  | {
      op: "new_template";
      name: string;
      description?: string;
      tags?: string[];
      kind: ListKind;
      sections: AiProposalTemplateSection[];
    }
  | {
      op: "new_checklist";
      name: string;
      description?: string;
      tags?: string[];
      /** May be a "$ref:N" back-reference to a new_template op earlier in the proposal. */
      templateId?: string;
      sections: AiProposalChecklistSection[];
    }
  | {
      op: "check_state";
      target: AiProposalTarget;
      checked: string[];
      unchecked: string[];
    };

/** A stored, reviewable set of ops. */
export interface AiProposal {
  proposalId: string;
  summary: string;
  ops: AiProposalOp[];
  createdAt: string;
}

/** One stored proposal operation selected by the user for application. */
export interface AiProposalSelectionEntry {
  /** Zero-based position in the immutable stored proposal `ops` array. */
  opIndex: number;
  /**
   * Only valid for an `items_change` operation. The zero-based positions of
   * its `changes` entries to apply. Omit to select every change in that op.
   */
  changeIndexes?: number[];
}

export interface AiApplyProposalRequest {
  /** Omitted only by legacy callers; the server treats omission as all stored ops. */
  selection?: AiProposalSelectionEntry[];
}

/** One-level undo state for the last Apply in a conversation. */
export type AiUndoState = "applied" | "undone";

export interface AiUndoResourceRef {
  kind: ResourceKind;
  id: string;
  name?: string;
}

export interface AiUndoView {
  proposalId: string;
  summary: string;
  ops: AiProposalOp[];
  state: AiUndoState;
  appliedAt: string;
  expiresAt: string;
  created: AiUndoResourceRef[];
  mutated: AiUndoResourceRef[];
}

export interface AiUndoConfirmRequest {
  confirm?: boolean;
}

export interface AiUndoResult {
  proposalId: string;
  state: AiUndoState;
  created: AiUndoResourceRef[];
  mutated: AiUndoResourceRef[];
}

export interface AiUndoConfirmRequired {
  error: "UNDO_CONFIRM_REQUIRED";
  dirty: boolean;
  created: AiUndoResourceRef[];
  mutated: AiUndoResourceRef[];
}

/**
 * Prefix for a forward reference to a resource created by an earlier op in the
 * SAME proposal, by op index: "$ref:0" is the id of op 0's created resource.
 * This is what lets a split-list proposal emit N new lists and then a template
 * that composes them, in one Apply.
 */
export const AI_PROPOSAL_REF_PREFIX = "$ref:";

/** Parse "$ref:2" -> 2. Returns null for a literal id. */
export function parseProposalRef(value: string): number | null {
  if (!value.startsWith(AI_PROPOSAL_REF_PREFIX)) return null;
  const n = Number(value.slice(AI_PROPOSAL_REF_PREFIX.length));
  return Number.isInteger(n) && n >= 0 ? n : null;
}

/**
 * Indexes of the earlier ops in the same stored proposal that this op's "$ref:N"
 * values point at. Applying the op without them would silently drop a template
 * section or fail the checklist, so selection enforces them (server) and cascades
 * them (browser).
 */
export function proposalOpDependencies(op: AiProposalOp): number[] {
  const refs: number[] = [];
  if (op.op === "new_template") {
    for (const section of op.sections) {
      if (section.type !== "list") continue;
      const n = parseProposalRef(section.listId);
      if (n !== null && !refs.includes(n)) refs.push(n);
    }
  } else if (op.op === "new_checklist" && op.templateId) {
    const n = parseProposalRef(op.templateId);
    if (n !== null) refs.push(n);
  }
  return refs;
}

// ---- Tickets (F-12, SA6; AI_ASSISTANT_REMEDIATION_PLAN Task 5.3) ----
//
// A draft_ticket call never submits anything. It shows the user a card built
// from this draft; the browser submits it through the site's existing,
// Turnstile-protected feedback intake, exactly as a human-filled report would.

export type AiTicketType = "bug" | "feature" | "contact";

export interface AiTicketDraft {
  type: AiTicketType;
  title: string;
  category?: string;
  severity?: "low" | "normal" | "high";
  steps?: string;
  expected?: string;
  actual?: string;
}

/**
 * The chat Lambda's timeout (infra/lib/checklist-stack.ts reads it from here). The
 * web panel's watchdog runs a margin past it (site-assistant SA24), so a turn the
 * Lambda dropped ends in an error instead of waiting dots that never stop.
 */
export const AI_CHAT_TIMEOUT_SECONDS = 120;

export type AiChatEvent =
  | { type: "session"; sessionId: string }
  | { type: "delta"; text: string }
  | { type: "tool"; name: string; status: "running" | "done" }
  | { type: "proposal"; proposalId: string; summary: string; ops: AiProposalOp[] }
  | { type: "link"; route: string; label: string }
  | { type: "ticket"; cardId: string; draft: AiTicketDraft; conversationRef: string }
  | {
      type: "debug";
      modelId: string;
      inTokens: number;
      outTokens: number;
      cacheReadTokens: number;
      cacheCreationTokens: number;
      rounds: number;
      durationMs: number;
      estCostUsd: number;
    }
  | { type: "error"; code: string; message: string }
  | { type: "done"; assistantSeq?: number };

// ---- Tool schemas (Anthropic shape: name / description / input_schema) ----

/** Anthropic tool definition. Kept structural so the array stays JSON-literal. */
export interface AiToolSchema {
  name: string;
  description: string;
  input_schema: Record<string, unknown>;
}

const RESOURCE_KIND_ENUM = ["list", "template", "checklist"] as const;

/**
 * The four read/grounding tools (D-A2). These execute server-side in-loop; they
 * only ever read, and always through the D-27 authorization seams.
 */
export const AI_CHAT_READ_TOOLS: readonly AiToolSchema[] = [
  {
    name: "read_resource",
    description:
      "Read one of the caller's lists, templates, or checklists in full: name, " +
      "description, tags, kind, and every item (including subgroups or sections). " +
      "Use this when the user refers to something you do not already have in context.",
    input_schema: {
      type: "object",
      properties: {
        kind: { type: "string", enum: RESOURCE_KIND_ENUM },
        id: { type: "string", description: "The resource id." },
      },
      required: ["kind", "id"],
    },
  },
  {
    name: "search_my_lists",
    description:
      "Search the caller's own lists and templates by name, description, or tag. " +
      "Returns summaries only (no items) so results never flood the conversation. " +
      "Call this before inventing content, so you can reuse what the user already has.",
    input_schema: {
      type: "object",
      properties: {
        query: {
          type: "string",
          description:
            "Case-insensitive substring matched against name, description, and tags. " +
            "Omit to list everything the caller owns.",
        },
        kind: {
          type: "string",
          enum: ["list", "template"],
          description: "Restrict to lists or templates. Omit for both.",
        },
      },
      required: [],
    },
  },
  {
    name: "search_community",
    description:
      "Search lists other people have published to the CheckList community. " +
      "Returns summaries plus a few sample items. Use this for grounding when the " +
      "user asks for something you have no first-hand knowledge of.",
    input_schema: {
      type: "object",
      properties: {
        query: { type: "string", description: "Search text." },
      },
      required: ["query"],
    },
  },
  {
    name: "get_list_items",
    description:
      "Read the items of a single list — either one the caller owns or one " +
      "published to the community. Use after search_community to see a published " +
      "list's full contents before suggesting anything from it.",
    input_schema: {
      type: "object",
      properties: {
        id: { type: "string", description: "The list id." },
      },
      required: ["id"],
    },
  },
] as const;

/** Names of the read tools, for dispatch and for the write/read split in the loop. */
export const AI_CHAT_READ_TOOL_NAMES: readonly string[] = AI_CHAT_READ_TOOLS.map(
  (t) => t.name
);

// ---- Write tools (D-A3): these NEVER save. Each emits one reviewable op. ----
//
// Appended after the four read tools, never interleaved with them (I-2), so the
// cached tools prefix built in Phase 2 stays a prefix of the Phase 3 array.
//
// Every description states plainly that the tool proposes rather than saves. The
// model is otherwise strongly inclined to narrate a write as done, which is the
// single most damaging thing it can say here.

/** The sentence appended to every write tool's description. */
const PROPOSES_ONLY =
  " This PROPOSES a change for the user to review; it does not save anything. " +
  "Nothing is written until the user presses Apply, so never tell them it is done.";

const TARGET_SCHEMA = {
  type: "object",
  description: "The existing resource to change.",
  properties: {
    kind: { type: "string", enum: RESOURCE_KIND_ENUM },
    id: { type: "string" },
  },
  required: ["kind", "id"],
} as const;

const STRING_ARRAY = { type: "array", items: { type: "string" } } as const;

const LIST_KIND_SCHEMA = {
  type: "string",
  enum: ["packing", "todo"],
  description:
    "'packing' for things to bring or gather, 'todo' for things to do. Match the " +
    "user's existing resource when extending one.",
} as const;

const GROUP_ARRAY_SCHEMA = {
  type: "array",
  description: "Named subgroups, one level only.",
  items: {
    type: "object",
    properties: {
      name: { type: "string" },
      items: STRING_ARRAY,
    },
    required: ["name", "items"],
  },
} as const;

export const AI_CHAT_WRITE_TOOLS: readonly AiToolSchema[] = [
  {
    name: "propose_items_change",
    description:
      "Add, remove, or reword items on an existing list or checklist. Use one call " +
      "with all the changes rather than several calls. Items are addressed by their " +
      "exact current text, not by id." + PROPOSES_ONLY,
    input_schema: {
      type: "object",
      properties: {
        target: {
          ...TARGET_SCHEMA,
          properties: {
            kind: { type: "string", enum: ["list", "checklist"] },
            id: { type: "string" },
          },
        },
        changes: {
          type: "array",
          items: {
            type: "object",
            properties: {
              op: { type: "string", enum: ["add", "remove", "edit"] },
              text: {
                type: "string",
                description:
                  "For 'add', the new item text. For 'remove' and 'edit', the exact " +
                  "existing item text to act on.",
              },
              newText: {
                type: "string",
                description: "'edit' only: the replacement text.",
              },
              note: {
                type: "string",
                description: "Optional short note attached to the item.",
              },
              group: {
                type: "string",
                description:
                  "'add' only: the subgroup (list) or section (checklist) to add it " +
                  "to. Omit for the ungrouped items / the first section.",
              },
            },
            required: ["op", "text"],
          },
        },
      },
      required: ["target", "changes"],
    },
  },
  {
    name: "propose_grouping",
    description:
      "Reorganize a list's, template's, or checklist's items into named categories. State " +
      "the WHOLE layout: every group with its items, plus whatever stays ungrouped. Match " +
      "the open resource's kind (list, template, or checklist) and id. Items are matched " +
      "by their exact current text without any '[x]' / '[ ]' prefix; anything you leave " +
      "out stays ungrouped. On a list or checklist, groups become one-level subgroups (on " +
      "a checklist, items stay in their current section). On a template, groups become " +
      "named inline sections; sections that reference a list are grouped on that list." +
      PROPOSES_ONLY,
    input_schema: {
      type: "object",
      properties: {
        target: {
          ...TARGET_SCHEMA,
          properties: {
            kind: { type: "string", enum: ["list", "template", "checklist"] },
            id: { type: "string" },
          },
        },
        subgroups: GROUP_ARRAY_SCHEMA,
        ungrouped: {
          ...STRING_ARRAY,
          description: "Items that should stay outside any subgroup.",
        },
        section: {
          type: "string",
          description:
            "Optional. Checklist: regroup just this named section. Template: regroup just " +
            "this named section (inline items, or the list it references). Omit to regroup everything.",
        },
      },
      required: ["target", "subgroups"],
    },
  },
  {
    name: "propose_metadata",
    description:
      "Set the name, description, or tags of an existing list, template, or checklist. " +
      "Use this to write the description a resource is missing, which is what makes it " +
      "findable and understandable later." + PROPOSES_ONLY,
    input_schema: {
      type: "object",
      properties: {
        target: TARGET_SCHEMA,
        name: { type: "string" },
        description: {
          type: "string",
          description: "One or two sentences on what the resource is for.",
        },
        tags: {
          ...STRING_ARRAY,
          description: "Replaces the existing tags entirely. Lowercase, short.",
        },
      },
      required: ["target"],
    },
  },
  {
    name: "propose_new_list",
    description:
      "Create a new reusable list. Emit this once per list; a request to split one " +
      "list into several means several calls, plus one propose_new_template that " +
      "composes them." + PROPOSES_ONLY,
    input_schema: {
      type: "object",
      properties: {
        name: { type: "string" },
        description: { type: "string" },
        tags: STRING_ARRAY,
        kind: LIST_KIND_SCHEMA,
        items: { ...STRING_ARRAY, description: "Items outside any subgroup." },
        subgroups: GROUP_ARRAY_SCHEMA,
      },
      required: ["name", "kind"],
    },
  },
  {
    name: "propose_new_template",
    description:
      "Create a new template composing lists into sections. A section either " +
      "references an existing list by id, or holds inline items of its own. To " +
      'reference a list proposed earlier in this same reply, use "$ref:N" where N ' +
      "is the ref number the tool result gave that propose_new_list call (every " +
      "propose_* call in a reply is numbered 0, 1, 2... in the order you make it)." +
      PROPOSES_ONLY,
    input_schema: {
      type: "object",
      properties: {
        name: { type: "string" },
        description: { type: "string" },
        tags: STRING_ARRAY,
        kind: LIST_KIND_SCHEMA,
        sections: {
          type: "array",
          items: {
            type: "object",
            properties: {
              type: { type: "string", enum: ["list", "inline"] },
              listId: {
                type: "string",
                description:
                  'A "list" section only: an existing list id, or "$ref:N".',
              },
              name: {
                type: "string",
                description: 'An "inline" section only: the section name.',
              },
              items: {
                ...STRING_ARRAY,
                description: 'An "inline" section only: its items.',
              },
            },
            required: ["type"],
          },
        },
      },
      required: ["name", "kind", "sections"],
    },
  },
  {
    name: "propose_new_checklist",
    description:
      "Create a new checklist: a named, checkable copy the user actually works " +
      "through. Use this whenever the user asks for a checklist. Give either " +
      'templateId or sections, not both. templateId is an existing template id or ' +
      '"$ref:N" for a template proposed earlier in this same reply - never a list ' +
      "id. Sections alone need no list or template at all." + PROPOSES_ONLY,
    input_schema: {
      type: "object",
      properties: {
        name: { type: "string" },
        description: { type: "string" },
        tags: STRING_ARRAY,
        templateId: {
          type: "string",
          description:
            'Instantiate this template. An existing template id, or "$ref:N" for a ' +
            "propose_new_template call earlier in this reply. Never a list id.",
        },
        sections: {
          type: "array",
          description: "Used only when templateId is omitted.",
          items: {
            type: "object",
            properties: {
              name: { type: "string" },
              items: STRING_ARRAY,
            },
            required: ["name", "items"],
          },
        },
      },
      required: ["name"],
    },
  },
  {
    name: "propose_check_state",
    description:
      "Tick or untick items on a checklist, addressed by their exact item text. Use " +
      "this for requests like 'mark everything in the kitchen section done'." +
      PROPOSES_ONLY,
    input_schema: {
      type: "object",
      properties: {
        target: {
          ...TARGET_SCHEMA,
          properties: {
            kind: { type: "string", enum: ["checklist"] },
            id: { type: "string" },
          },
        },
        checked: { ...STRING_ARRAY, description: "Item texts to tick." },
        unchecked: { ...STRING_ARRAY, description: "Item texts to untick." },
      },
      required: ["target"],
    },
  },
] as const;

/** Names of the write tools, for the read/write split in the streaming loop. */
export const AI_CHAT_WRITE_TOOL_NAMES: readonly string[] = AI_CHAT_WRITE_TOOLS.map(
  (t) => t.name
);

// ---- Knowledge-base and card tools (F-10, F-12, SA6; Task 5.3) ----
//
// Appended after the eleven Phase 2/3 tools (I-2): the cached tools prefix built
// then is a strict prefix of this array, so its cache entry stays valid.
//
// search_kb / read_kb_section execute in-loop like the read tools (D-A2) - they
// only ever read the committed KB bundle. navigate_to and draft_ticket are
// "card" tools: neither reads app data nor proposes a write: navigate_to just
// shows a link, and draft_ticket shows a reviewable ticket draft the browser
// submits through the site's existing, Turnstile-protected intake (SA6) - never
// through this Lambda.

export const AI_CHAT_KB_TOOLS: readonly AiToolSchema[] = [
  {
    name: "search_kb",
    description:
      "Search CheckList's help notes for how a feature works, where to find something, " +
      "or a policy. Call this before answering any question about how CheckList works.",
    input_schema: {
      type: "object",
      properties: {
        q: { type: "string", description: "Search text." },
      },
      required: ["q"],
    },
  },
  {
    name: "read_kb_section",
    description:
      "Read one help-notes section in full, by the id a search_kb result gave you.",
    input_schema: {
      type: "object",
      properties: {
        id: { type: "string", description: "A section id from a search_kb result." },
      },
      required: ["id"],
    },
  },
] as const;

/** Names of the KB read tools, folded into the "read" class (Task 5.3). */
export const AI_CHAT_KB_TOOL_NAMES: readonly string[] = AI_CHAT_KB_TOOLS.map((t) => t.name);

export const AI_CHAT_CARD_TOOLS: readonly AiToolSchema[] = [
  {
    name: "navigate_to",
    description:
      "Show the user a link to a CheckList page. Use this every time you mention a " +
      "specific screen, so they can go there directly.",
    input_schema: {
      type: "object",
      properties: {
        route: {
          type: "string",
          description: "A CheckList app path, for example /lists or /lists/abc123.",
        },
        label: { type: "string", description: "Short link text, at most 60 characters." },
      },
      required: ["route", "label"],
    },
  },
  {
    name: "draft_ticket",
    description:
      "Draft a support ticket for the user to review and submit themselves. Search the " +
      "help notes first, and offer at most two drafts in one conversation. This shows a " +
      "card; nothing is sent until the user reviews and submits it.",
    input_schema: {
      type: "object",
      properties: {
        type: { type: "string", enum: ["bug", "feature", "contact"] },
        title: { type: "string" },
        category: { type: "string" },
        severity: { type: "string", enum: ["low", "normal", "high"] },
        steps: { type: "string", description: "Steps to reproduce, for a bug report." },
        expected: { type: "string" },
        actual: { type: "string" },
      },
      required: ["type", "title"],
    },
  },
] as const;

/** Names of the card tools: shown to the user, never executed as a read or a write. */
export const AI_CHAT_CARD_TOOL_NAMES: readonly string[] = AI_CHAT_CARD_TOOLS.map(
  (t) => t.name
);

/** Read, write, card, or a name the model hallucinated. */
export type AiToolClass = "read" | "write" | "card" | "unknown";

export function classifyAiTool(name: string): AiToolClass {
  if (AI_CHAT_READ_TOOL_NAMES.includes(name) || AI_CHAT_KB_TOOL_NAMES.includes(name)) {
    return "read";
  }
  if (AI_CHAT_WRITE_TOOL_NAMES.includes(name)) return "write";
  if (AI_CHAT_CARD_TOOL_NAMES.includes(name)) return "card";
  return "unknown";
}

/** Write tool name -> the AiProposalOp discriminant it produces. */
export const AI_CHAT_WRITE_TOOL_OPS: Readonly<Record<string, AiProposalOp["op"]>> = {
  propose_items_change: "items_change",
  propose_grouping: "grouping",
  propose_metadata: "metadata",
  propose_new_list: "new_list",
  propose_new_template: "new_template",
  propose_new_checklist: "new_checklist",
  propose_check_state: "check_state",
};

/**
 * The complete tool array sent to Bedrock, in fixed order (I-2): the four read
 * tools first, exactly as Phase 2 sent them, then the seven write tools, then
 * the two KB tools and the two card tools (Task 5.3). Appending rather than
 * reordering is what keeps every earlier cached tools prefix valid.
 */
export const AI_CHAT_TOOLS: readonly AiToolSchema[] = [
  ...AI_CHAT_READ_TOOLS,
  ...AI_CHAT_WRITE_TOOLS,
  ...AI_CHAT_KB_TOOLS,
  ...AI_CHAT_CARD_TOOLS,
];

/**
 * Routes navigate_to is allowed to link to: the static routes in web/src/App.tsx,
 * plus the parameterized resource-editor and community-template routes (Task 5.3).
 */
export const AI_NAV_ROUTE_PATTERNS: readonly RegExp[] = [
  /^\/lists$/,
  /^\/templates$/,
  /^\/checklists$/,
  /^\/community$/,
  /^\/profile$/,
  /^\/contact$/,
  /^\/privacy$/,
  /^\/how-it-works$/,
  /^\/admin\/settings$/,
  /^\/admin\/ai-costs$/,
  /^\/(lists|templates|checklists)\/[A-Za-z0-9_-]{1,64}$/,
  /^\/community\/templates\/[A-Za-z0-9_-]{1,64}$/,
];

/** Item text cap the model is told about, so it never proposes over-long items. */
export const AI_CHAT_ITEM_TEXT_MAX = LIMITS.itemTextMax;
